/**
 * A mesa de jogos da Sala de Descanso — o desenho.
 *
 * Vive fora do `officeArt.ts` pela mesma razão do tapete: a arte deste
 * escritório é um ficheiro de milhares de linhas, e uma peça nova que se
 * explique a si própria não tem de lá entrar para ser desenhada. Aqui só se
 * importa um `Brush` — declarado outra vez, com a forma exacta do original — o
 * que evita um ciclo de importação e mantém a peça auto-contida.
 *
 * O que a mesa TEM de comunicar em 64×52 pixels: que ali se joga, e o QUÊ. Um
 * tampo vazio dizia "mesa"; o que diz "4 em linha" é a grelha AZUL EM PÉ com
 * discos vermelhos e amarelos lá dentro — a silhueta do jogo é tão conhecida
 * que se lê à primeira mesmo com 28 px de largura. É por isso que o tabuleiro
 * ergue a peça 20 px acima da pegada em vez de ficar deitado no tampo, onde a
 * perspectiva 3/4 do escritório o esmagaria numa mancha sem furos.
 */

/** O pincel do `officeArt` — redeclarado com a MESMA forma para não importar
 *  o módulo grande só por um tipo (e não criar um ciclo entre os dois). */
export interface Brush {
  r(x: number, y: number, w: number, h: number, color: string): void;
  dither(x: number, y: number, w: number, h: number, a: string, b: string): void;
}

// ── Geometria da grelha ─────────────────────────────────────
//
// Tudo aqui é INTEIRO de propósito. A textura corre com `pixelArt: true`, e uma
// célula de 4,57 px (que é o que dava dividir 32 por 7) punha o `fillRect` em
// coordenadas fraccionárias — o canvas resolve-as com uma franja cinzenta em
// cada furo, e um tabuleiro com 42 furos ficava com 42 borrões. Escolhe-se a
// célula primeiro (4) e é a largura do tabuleiro que se deixa cair onde calha.

/** Colunas do tabuleiro — as mesmas 7 das regras (`C4_COLS`). */
const COLS = 7;
/** Linhas do tabuleiro — as mesmas 6 das regras (`C4_ROWS`). */
const ROWS = 6;
/** Lado de uma casa. 4 px: 3 de furo + 1 de azul a separar. */
const CELL = 4;
/** Canto superior-esquerdo do interior da grelha. */
const GRID_X = 18;
const GRID_Y = 2;
/** Espessura da moldura de plástico à volta dos furos. */
const FRAME = 2;

const AZUL = "#2a52a8";
const AZUL_HI = "#4a78d4";
const AZUL_SH = "#1b3572";
const VAZIO = "#1a2740"; // o furo às escuras — vê-se a parede do outro lado
const VERMELHO = "#d0392f";
const VERMELHO_HI = "#f06a5c";
const AMARELO = "#e8b423";
const AMARELO_HI = "#ffd863";
const MADEIRA = "#5a3b26";
const MADEIRA_HI = "#7d5537";
const MADEIRA_MID = "#6a4630";
const MADEIRA_SH = "#3e2818";
const MADEIRA_DK = "#241609";

/**
 * O tabuleiro tal como se encontra a mesa: uma partida a meio.
 *
 * Não é decoração aleatória — é uma posição LEGAL (as peças assentam umas nas
 * outras, e há tantas vermelhas como amarelas ou uma a mais). Uma peça a pairar
 * sobre um furo vazio seria o género de erro que ninguém consegue não ver
 * depois de o ver uma vez. Linha 0 é o TOPO, como nas regras.
 */
// biome-ignore format: uma linha por linha do tabuleiro — colapsado numa só, o
// desenho deixa de se ler como o que é e um erro de gravidade passa despercebido.
export const POSICAO: readonly string[] = [
  ".......",
  ".......",
  ".......",
  "...R...",
  "...A...",
  "..RAR..",
];

/** Um disco de 3×3 com brilho no canto superior-esquerdo — a leitura de
 *  "peça redonda de plástico" que cabe em nove pixels. */
function disco(b: Brush, x: number, y: number, cor: string, brilho: string): void {
  b.r(x, y, 3, 3, cor);
  b.r(x, y, 1, 1, brilho);
  b.r(x + 1, y, 1, 1, brilho);
  b.r(x, y + 1, 1, 1, brilho);
}

/** A grelha em pé: moldura, os 42 furos e as peças da posição inicial. */
function tabuleiro(b: Brush): void {
  const w = COLS * CELL;
  const h = ROWS * CELL;

  // Moldura: azul cheio, luz em cima e à esquerda, sombra em baixo e à direita.
  b.r(GRID_X - FRAME, GRID_Y - FRAME, w + FRAME * 2, h + FRAME * 2, AZUL);
  b.r(GRID_X - FRAME, GRID_Y - FRAME, w + FRAME * 2, 1, AZUL_HI);
  b.r(GRID_X - FRAME, GRID_Y - FRAME, 1, h + FRAME * 2, AZUL_HI);
  b.r(GRID_X - FRAME, GRID_Y + h + FRAME - 1, w + FRAME * 2, 1, AZUL_SH);
  b.r(GRID_X + w + FRAME - 1, GRID_Y - FRAME, 1, h + FRAME * 2, AZUL_SH);

  for (let r = 0; r < ROWS; r += 1) {
    const linha = POSICAO[r] ?? "";
    for (let c = 0; c < COLS; c += 1) {
      const x = GRID_X + c * CELL;
      const y = GRID_Y + r * CELL;
      const casa = linha[c];
      if (casa === "R") disco(b, x, y, VERMELHO, VERMELHO_HI);
      else if (casa === "A") disco(b, x, y, AMARELO, AMARELO_HI);
      else b.r(x, y, 3, 3, VAZIO);
    }
  }

  // Os dois pés que seguram a grelha em pé sobre o tampo.
  const base = GRID_Y + h + FRAME;
  for (const px of [GRID_X + 1, GRID_X + w - 6]) {
    b.r(px, base, 5, 3, AZUL_SH);
    b.r(px, base, 5, 1, AZUL);
  }
}

/** Uma pilha de peças de reserva pousada no tampo, vista de lado. */
function pilha(b: Brush, x: number, y: number, altura: number, cor: string, brilho: string): void {
  for (let i = 0; i < altura; i += 1) {
    const py = y - i * 2;
    b.r(x, py, 6, 2, cor);
    b.r(x, py, 6, 1, brilho);
  }
}

/**
 * A mesa completa. Grelha de arte 64×52 (registada como 32×26 a `detail: 2`),
 * com a pegada de 2×1 tiles nos ÚLTIMOS 32 px — os 20 de cima são o tabuleiro a
 * subir acima do chão que ocupa, como o beiral das mobile homes.
 */
export function drawGameTable(b: Brush): void {
  tabuleiro(b);

  // ── Tampo. Duas alturas para arredondar os cantos, como na mesa de café:
  // o rectângulo largo ao meio e o estreito por cima e por baixo.
  b.r(4, 30, 56, 8, MADEIRA);
  b.r(2, 32, 60, 4, MADEIRA);
  b.r(4, 30, 56, 2, MADEIRA_HI);
  b.r(2, 32, 2, 4, MADEIRA_MID);
  b.dither(8, 32, 48, 4, MADEIRA_MID, MADEIRA);
  b.r(10, 33, 40, 1, MADEIRA_HI); // reflexo de verniz, uma risca só
  b.r(4, 37, 56, 1, MADEIRA_DK);
  b.r(2, 35, 60, 1, MADEIRA_SH);

  // ── Pernas. As da frente apanham luz numa aresta; as de trás ficam na sombra
  // e mais curtas, que é o que dá a profundidade em vista 3/4.
  for (const lx of [6, 54]) {
    b.r(lx, 38, 4, 13, MADEIRA_SH);
    b.r(lx, 38, 1, 13, MADEIRA_MID);
    b.r(lx + 3, 38, 1, 13, MADEIRA_DK);
  }
  for (const lx of [14, 46]) b.r(lx, 38, 3, 9, MADEIRA_DK);

  // ── As reservas de cada jogador, uma pilha de cada lado do tabuleiro.
  pilha(b, 6, 28, 4, VERMELHO, VERMELHO_HI);
  pilha(b, 52, 28, 4, AMARELO, AMARELO_HI);
}
